/* eslint-disable array-callback-return */
import { useMemo, useState } from "react";
import { Group } from "@visx/group";
import { BarGroup } from "@visx/shape";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { scaleBand, scaleLinear, scaleOrdinal } from "@visx/scale";
import { Grid } from "@visx/grid";
import useMeasure from "react-use-measure";
import {
  HeaderWrapper,
  HeadingNameAndButton,
  Title,
} from "../VerticalBarchart/styles";
import { ChartLegend, ChartLegendValue } from "../../ChartLegend";
import { setActiveModal } from "../../../store/slices/modals";
import { useDispatch, useSelector } from "react-redux";
import { getIsEditMode, getIsPublicMode } from "../../../store/selectors/main";
import { ticksFormatter } from "../../../helpers/ticksFormatter";
import { getPageSettings } from "../../../store/selectors/projects";
import { getAiSuggestions } from "../../../store/selectors/widgets";
import { AiSuggestionsDto, WidgetItem } from "../../../models/Widgets";
import { Tooltip, TooltipProps } from "../Tooltip";
import { Loader } from "../../Loader";
import {
  calculateLabelLength,
  calculateNumTicks,
  getAvailableWidgetTypes,
  getScaleBandTickValues,
} from "../widgetHelpers";
import { setCurrentWidget } from "../../../store/slices/projectPages";
import { SelectBage } from "../SelectBage";
import { replaceWords } from "../../../helpers/replaceName";
import { getBarChartKeys } from "./utils/getKeys";
import { getCurrentColorV2 } from "../utils/getCurrentMarker";
import { createPortal } from "react-dom";
import { Rect } from "./styles";
import { getBarPosition } from "./utils/getBarPosition";
import { TickLabel } from "../components/LabelTooltip";
import { SettingsButtonWrapper, DatavizRecommendedCount, DatavizSettingsIcon } from "../styles";

export type BarGroupProps = {
  storytelling?: boolean;
  currentWidget: WidgetItem;
  recommended?: boolean;
  showLegend?: boolean;
  selected?: boolean;
  hideName?: boolean;
  preview?: boolean;
  isRight?: boolean;
};

type DataItem = {
  year: string;
  [key: string]: string;
  value: string;
};

export const GroupedBarChart = ({
  storytelling,
  recommended,
  currentWidget,
  showLegend = true,
  selected = false,
  hideName = false,
  preview = false,
  isRight,
}: BarGroupProps) => {
  const isEditMode = useSelector(getIsEditMode);
  const [ref, bounds] = useMeasure();
  const [refWidget, boundsWidget] = useMeasure();

  const isPublicRoute = useSelector(getIsPublicMode);
  const dispatch = useDispatch();
  const { styleId } = useSelector(getPageSettings);
  const aiSuggestions = useSelector(getAiSuggestions);
  const [tooltip, setTooltip] = useState<TooltipProps | null>(null);
  const [hoveredBlock, setHoveredBlock] = useState<null | string>(null);

  const barChartSuggestion = aiSuggestions
    ?.filter((chart: AiSuggestionsDto) => chart.chartType === "barChart")
    .find((el: any) => el.groupBy !== null);

  const name = useMemo(() => {
    return recommended
      ? replaceWords(currentWidget?.name)
      : currentWidget?.name;
  }, [currentWidget?.name, recommended]);

  const availableWidgetsCount = useMemo(() => {
    if (recommended) {
      return 0;
    }

    return getAvailableWidgetTypes(currentWidget).length;
  }, [currentWidget, recommended]);

  if (!(currentWidget && currentWidget?.data)) {
    return (
      <>
        <div style={{ height: "100%", width: "100%" }}>
          <Loader blur={false} />
        </div>
      </>
    );
  }

  const groupBy = currentWidget?.groupBy?.at(0);
  const data: any = currentWidget?.data;

  const margin = { top: 10, right: 5, bottom: 21, left: 40 };
  const width = bounds.width || 1084;
  const height = bounds.height || 300;

  const minBarWidth = 4;
  const maxBarWidth = 24;

  const yMax = height - margin.top - margin.bottom;
  const xMax = width - margin.left - margin.right;

  const xAxe = currentWidget?.xAxe?.length
    ? currentWidget?.xAxe?.at(0)
    : barChartSuggestion?.xAxe?.at(0);
  const yAxe = currentWidget?.yAxe?.length
    ? currentWidget?.yAxe?.at(0)
    : barChartSuggestion?.yAxe?.at(0);

  const uniqueValuesKeys =
    (currentWidget?.uniqueValues &&
      Object.keys(currentWidget?.uniqueValues!)) ||
    [];
  const groupByKey =
    groupBy && groupBy?.length ? groupBy : uniqueValuesKeys?.at(0);

  const keys = (
    uniqueValuesKeys?.length && currentWidget?.uniqueValues
      ? currentWidget?.uniqueValues[groupByKey!]
      : getBarChartKeys(currentWidget)
  ).map((d) => String(d));

  const formattedData = data?.map((item: any) => {
    return {
      [xAxe]: item[xAxe],
      ...keys?.reduce((acc: any, key: any) => {
        acc[key] = item[groupBy!] === key ? Number(item[yAxe]) : 0;
        return acc;
      }, {}),
    };
  });

  const xAxes: string[] =
    currentWidget?.uniqueValues?.[xAxe] ||
    Array.from(new Set(formattedData.map((d: DataItem) => String(d[xAxe])))) ||
    [];

  const getValue = (d: DataItem) => Number(d[yAxe]);

  const numBarsInGroup = keys.length;
  const fixedBarSpacing = 1;

  const calculatedBarWidth = Math.floor(
    Math.min(
      Math.max(xMax / xAxes?.length / numBarsInGroup, minBarWidth),
      maxBarWidth
    )
  );

  const totalSpaceRequiredPerGroup =
    numBarsInGroup * calculatedBarWidth +
    (numBarsInGroup - 1) * fixedBarSpacing;

  const xScale = scaleBand<string>({
    domain: xAxes,
    range: [0, width - margin.left - margin.right],
    align: 0.5,
    padding: 0,
  });

  const keysScale = scaleBand<string>({
    domain: keys,
    range: [0, totalSpaceRequiredPerGroup],
    paddingInner: 0.1,
    paddingOuter: 0,
  });

  const maxYAxe = Math.max(...data?.map(getValue));
  const yScale = scaleLinear<number>({
    domain: [0, maxYAxe],
    range: [yMax, 0],
    nice: true,
  });

  const legendValues: ChartLegendValue[] = [];

  for (let i = 0; i < keys?.length; i++) {
    const dataKey = keys?.at(i);
    legendValues.push({
      label: dataKey!,
      color: getCurrentColorV2(currentWidget, dataKey!, styleId),
    });
  }

  const colorScale = scaleOrdinal<string, string>({
    domain: keys,
    range: legendValues?.map((r) => r.color),
  });

  const yScaleTickValues = calculateNumTicks({ height: height });

  const xScaleNumTicksCalculated = calculateNumTicks({ width });

  const isReasonableAmountOfTicks =
    xScaleNumTicksCalculated <= xAxes.length &&
    xScaleNumTicksCalculated > 0 &&
    xAxes.length / xScaleNumTicksCalculated >= 1.5;

  const xScaleNumTicks = isReasonableAmountOfTicks
  ? xScaleNumTicksCalculated
  : xAxes.length;

  const xScaleTickValues = getScaleBandTickValues({
    tickCount: xScaleNumTicks,
    ticks: xAxes,
  });

  const xScaleTickLabelMaxLength = calculateLabelLength({
    width: width - margin.left - margin.right,
    tickValues: xScaleTickValues,
  });

  return (
    <>
      <HeaderWrapper ref={refWidget}>
        {!storytelling && (
          <HeadingNameAndButton>
            {!hideName ? <Title>{name}</Title> : <div />}
            {!isPublicRoute && !recommended && isEditMode && !storytelling ? (
              <SettingsButtonWrapper
                $modalOpen={false}
                onClick={() => {
                  dispatch(setCurrentWidget(currentWidget!));
                  dispatch(setActiveModal({ id: "recommendedWidgetsModal" }));
                }}
              >
                <DatavizRecommendedCount>
                  {availableWidgetsCount}
                </DatavizRecommendedCount>
                <DatavizSettingsIcon />
              </SettingsButtonWrapper>
            ) : null}
            {recommended ? <SelectBage selected={selected} /> : null}
          </HeadingNameAndButton>
        )}
        {legendValues?.length > 1 && showLegend && currentWidget?.legend && (
          <ChartLegend
            chartWidth={boundsWidget.width}
            legendType="unit"
            legendValues={legendValues}
            isRight={isRight}
          />
        )}
      </HeaderWrapper>
      <svg ref={ref} width="100%" height="100%">
        <Grid
          top={margin.top}
          left={margin.left}
          xScale={xScale}
          yScale={yScale}
          numTicksColumns={xScaleNumTicks}
          numTicksRows={yScaleTickValues}
          width={xMax}
          height={yMax}
          stroke="#ccc"
          strokeOpacity={1}
          strokeDasharray="1 2"
          xOffset={xScale.bandwidth() / 2}
        />
        <AxisLeft
          scale={yScale}
          top={margin.top + 1}
          left={margin.left}
          stroke="#e0e0e0"
          strokeDasharray="1,2"
          tickStroke="#e0e0e0"
          numTicks={yScaleTickValues}
          tickLineProps={{
            stroke: "#939BA7",
          }}
          tickLabelProps={{
            dx: -30,
            fontSize: 11,
            fill: "#5F6877",
            textAnchor: "start",
            dy: 4,
          }}
          tickFormat={(value: any) => {
            return ticksFormatter(value);
          }}
        />
        <AxisBottom
          top={yMax + margin.top + 1}
          scale={xScale}
          left={margin.left}
          stroke="#e0e0e0"
          tickStroke="#e0e0e0"
          hideTicks
          axisLineClassName="barchartAxisLine"
          tickLabelProps={(_: any, index: number, values: any) => {
            const isFirstTick = index === 0;
            const isLastTick = index === values.length - 1;

            if (!isFirstTick && !isLastTick) {
              return {
                fontSize: 11,
                fill: "#5F6877",
                textAnchor: "middle",
              };
            }

            const charSpace = 8;
            const bandWidth = xScale.bandwidth();
            const labelText = values[index].formattedValue;

            const isLabelTooLong = labelText.length > bandWidth / charSpace;

            const anchorPosition =
              (isLabelTooLong &&
                ((isFirstTick && "start") || (isLastTick && "end"))) ||
              "middle";

            const tickPositionOffset =
              (isLabelTooLong &&
                ((isFirstTick && -bandWidth / 2) ||
                  (isLastTick && bandWidth / 2))) ||
              0;

            return {
              fontSize: 11,
              fill: "#5F6877",
              textAnchor: anchorPosition,
              dx: tickPositionOffset,
            };
          }}
          tickValues={xScaleTickValues}
          tickComponent={(props: any) => (
            <TickLabel
              {...props}
              length={xScaleTickLabelMaxLength}
              offsetX={-10}
            />
          )}
        />
        <Group top={margin.top} left={margin.left}>
          <BarGroup
            data={formattedData}
            keys={keys}
            height={yMax}
            x0={(d) => String(d[xAxe])}
            x0Scale={xScale}
            x1Scale={keysScale}
            yScale={yScale}
            color={colorScale}
          >
            {(barGroups) => {
              return barGroups.map((barGroup) => {
                return (
                  <Group
                    key={`bar-group-${barGroup.index}-${barGroup.x0}`}
                    left={barGroup.x0 + xScale.bandwidth() / 2}
                  >
                    {barGroup.bars.map((bar, index) => {
                      const legend = legendValues.find(
                        (r) => r.label === bar.key
                      );

                      const barWidth = Math.min(
                        Math.max(bar.width, minBarWidth),
                        maxBarWidth
                      );

                      const x =
                        0 -
                        barWidth / 2 +
                        getBarPosition(barWidth, bar.index, keys.length);

                      return (
                        <Rect
                          key={`bar-group-bar-${barGroup.index}-${bar.index}-${bar.value}-${bar.key}`}
                          x={x}
                          y={bar.y + 1}
                          width={barWidth}
                          height={bar.height}
                          fill={legend?.color}
                          opacity={
                            hoveredBlock
                              ? hoveredBlock ===
                                `bar-group-${barGroup.index}-${barGroup.x0}-${index}`
                                ? 1
                                : 0.2
                              : 1
                          }
                          onMouseMove={(event: any) => {
                            const key = `bar-group-${barGroup.index}-${barGroup.x0}-${index}`;

                            if (
                              currentWidget.tooltip &&
                              !recommended
                            ) {
                              const { pageX, pageY, clientX, clientY } = event;
                              const coords = { pageX, pageY, clientX, clientY };

                              setHoveredBlock(key);
                              const hoveredBarGroup =
                                formattedData[barGroup.index];

                              setTooltip({
                                name: bar.key,
                                data: {
                                  [xAxe]: hoveredBarGroup[xAxe],
                                  [groupBy!]: keys[bar.index],
                                  [yAxe]: bar?.value?.toString(),
                                },
                                coords,
                              });
                            }
                          }}
                          onMouseLeave={() => {
                            setTooltip(null);
                            setHoveredBlock(null);
                          }}
                        />
                      );
                    })}
                  </Group>
                );
              });
            }}
          </BarGroup>
        </Group>
      </svg>

      {tooltip &&
        createPortal(
          <Tooltip
            xAxe={xAxe}
            yAxe={yAxe}
            data={tooltip.data}
            name={tooltip.name}
            coords={tooltip.coords}
          />,
          document.body
        )}
    </>
  );
};
